import { t, Trans } from '@lingui/macro';
import { EyeOff, BellOff, Archive } from 'lucide-react';
import { FC, useState } from 'react';
import { useHistory } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { Room } from '../../models/Room';
import { RoomCall } from '../../models/RoomCall';
import { User } from '../../models/User';
import { My } from '../../services/My';
import { MiniCallBadge } from '../components/CallInfoItems';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { IconHash, IconSparkles } from '../components/FigmaIcons';

type IRoomItem = { room: Room, roomCall: RoomCall | undefined, isSelected: boolean, nrOfNotifs: number, theOtherUser: User | undefined, meInTheCall: boolean, pathEnd: string, disabledPush: boolean, isLegacy?: boolean };

const RoomItem: FC<IRoomItem> = ({ room, roomCall, isSelected, nrOfNotifs, theOtherUser, meInTheCall, pathEnd, disabledPush, isLegacy }) => {
  const history = useHistory();
  const { setIsMobileMenuOpen } = useAppUIContext();

  const isOneToOne = room.isOneToOne;
  const isPersonalRoom = room.visibility === 'personal';
  const roomName = isPersonalRoom ? t`My Channel` : Room.getRoomName(room, My.user.id);
  const iconName = isPersonalRoom ? '' : room.profileImageUrl ? room.profileImageUrl : room.isOneToOne ? theOtherUser?.photoUrl : '';

  const handleClick = async () => {
    const targetPath = `/channel/${room.id}`;
    if (history.location.pathname !== targetPath) {
      history.replace(targetPath);
      if (window.innerWidth < 1200) {
        setIsMobileMenuOpen(false);
      }
    }
  };

  // Figma: sidebar-menu → gap-1.5 p-1.5 rounded-control
  // Figma: selected → bg-[rgba(255,127,0,0.2)]
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 p-1.5 cursor-pointer select-none transition-all duration-150 rounded-control w-full",
        isSelected
          ? "bg-[rgba(255,127,0,0.2)] text-[color:rgb(var(--text-body))]"
          : isLegacy
            ? "text-muted-foreground hover:bg-accent hover:text-foreground"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
      onClick={handleClick}
    >
      {/* 1:1 채팅: 유저 아바타(네이티브 img) / 그룹 채팅: Figma hash 아이콘 12px */}
      {isOneToOne ? (
        <OneToOneAvatar iconName={iconName || ''} fallbackName={theOtherUser?.name || roomName.replace('# ', '')} />
      ) : (
        <IconHash className="w-3 h-3 text-muted-foreground shrink-0" strokeWidth={2.5} />
      )}

      {/* Figma: text-body leading-[24px] Pretendard Regular, color: Gray/700 (#3e444c) */}
      <div className="flex-1 min-w-0 flex items-center gap-1 overflow-hidden">
        <div className={cn(
          "truncate text-body leading-[24px]",
          isSelected ? "font-semibold text-[color:rgb(var(--text-body))]" : "font-normal",
          Room.isArchivedOneToOne(room) && "text-muted-foreground line-through"
        )}>
          {roomName}
        </div>
        {room.visibility === 'unlisted' && <EyeOff className="w-3 h-3 text-muted-foreground shrink-0" />}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {isLegacy && (
          <span className="text-meta-sm text-muted-foreground shrink-0">(<Trans>old</Trans>)</span>
        )}
        {isPersonalRoom && !isLegacy && (() => {
          const isAiMemoActive = history.location.pathname.startsWith('/ai-memo') && history.location.search.includes(`roomId=${room.id}`);
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/ai-memo?view=memos&roomId=${room.id}`);
                if (window.innerWidth < 1200) setIsMobileMenuOpen(false);
              }}
              className={cn(
                "flex items-center justify-center p-1 rounded-full transition-colors",
                isAiMemoActive
                  ? "bg-ai/20 text-ai"
                  : "hover:bg-ai/10 text-ai/70"
              )}
              title={t`AI Memo`}
            >
              <IconSparkles className="w-4 h-4" />
            </button>
          );
        })()}
        {roomCall && RoomCall.isActive(roomCall) && (
          <span>{MiniCallBadge(roomCall.userIds.length, meInTheCall ? false : true)}</span>
        )}
        {disabledPush && <BellOff className="w-4 h-4 text-muted-foreground shrink-0" />}
        {/* nrOfNotifs 는 노출 대상(음소거·외부 비활성화 제외)만 카운트 → 음소거 채널은 뱃지 안 뜸 */}
        {!!nrOfNotifs && (
          <Badge variant="notification">
            {nrOfNotifs}
          </Badge>
        )}
      </div>
    </div>
  );
};

// 네이티브 img 기반 아바타 — Radix Avatar의 loading 상태 루프 방지
const OneToOneAvatar: FC<{ iconName: string; fallbackName: string }> = ({ iconName, fallbackName }) => {
  const [imgError, setImgError] = useState(false);

  if (!iconName || imgError) {
    return (
      <div className="w-7 h-7 rounded-full shrink-0 bg-accent flex items-center justify-center text-muted-foreground text-meta-sm font-medium overflow-hidden">
        {fallbackName.substring(0, 1).toUpperCase()}
      </div>
    );
  }

  return (
    <img
      src={iconName}
      alt=""
      className="w-7 h-7 rounded-full shrink-0 object-cover"
      onError={() => setImgError(true)}
    />
  );
};

export default RoomItem;
